import React, { useEffect } from 'react';
import {
  Box,
  IconButton,
  Link,
  Stack,
  SxProps,
  Typography,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { Button } from 'system/atoms';
import { IconTooltip } from 'common/ui/IconToolTip';
import { TextBlock } from 'common/ui/TextBlock';
import {
  useListDocumentTypesByOrganizationIdQuery,
  useArchiveDocumentTypeMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { DocumentType } from 'generated-graphql/types';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { useConfirmationModal } from 'services/modals/hooks';
import { ConfirmationModalType } from 'services/modals/constants';
import { useLoadingOverlay } from 'services/loading/hooks';
import { useToast, ToastType } from 'services/toast';
import { workflowBuilderLink } from 'modules/layout/constants/navbar';
import { formatValidationSummary } from '../utils/formatValidationSummary';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import { DocumentTypeDrawer } from './DocumentTypeDrawer';

const styles = {
  container: { mt: 6 },
  header: { mb: 3 },
  list: { maxWidth: 600 },
  emptyState: { py: 3 },
  row: { py: 2, gap: 2, pl: 2 },
  rowText: { flex: 1, minWidth: 0 },
  rowActions: { gap: 2 },
  actionIcon: { color: 'primary.main' },
  addButtonWrapper: { mt: 3 }
} satisfies Record<string, SxProps>;

type Props = { organizationId: string };

export const DocumentTypeSection: React.FC<Props> = ({ organizationId }) => {
  const { showToast } = useToast();
  const { showModal } = useConfirmationModal();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { openDrawer, closeDrawer } = useDrawerV2();

  // Server-side filter on `isArchived` so we don't consume page space with
  // rows the UI would drop anyway — an org with lots of archived types could
  // otherwise fill a whole page with dead rows and look empty. Paired with the
  // `nextToken` walk below so pages beyond the first are also fetched.
  const { data, loading, refetch, fetchMore } =
    useListDocumentTypesByOrganizationIdQuery({
      variables: {
        organizationId,
        limit: 200,
        filter: { isArchived: { ne: true } }
      },
      fetchPolicy: 'cache-and-network'
    });

  const nextToken = data?.documentTypesByOrganizationIdAndCreatedAt?.nextToken;

  useEffect(() => {
    if (!nextToken) return;
    fetchMore({
      variables: { nextToken },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const prevPage = prev.documentTypesByOrganizationIdAndCreatedAt;
        const nextPage =
          fetchMoreResult.documentTypesByOrganizationIdAndCreatedAt;
        if (!prevPage || !nextPage) return fetchMoreResult;
        return {
          ...fetchMoreResult,
          documentTypesByOrganizationIdAndCreatedAt: {
            ...nextPage,
            items: [...(prevPage.items ?? []), ...(nextPage.items ?? [])]
          }
        };
      }
    });
  }, [nextToken, fetchMore]);

  const [archiveDocumentType] = useArchiveDocumentTypeMutation();

  const documentTypes = (
    data?.documentTypesByOrganizationIdAndCreatedAt?.items ?? []
  ).filter((dt): dt is DocumentType => Boolean(dt) && !dt?.isArchived);

  const openEditDrawer = (documentType?: DocumentType) => {
    openDrawer({
      width: '500px',
      children: (
        <DocumentTypeDrawer
          organizationId={organizationId}
          documentType={documentType}
          onClose={closeDrawer}
          onSaved={() => {
            refetch();
            closeDrawer();
          }}
          onArchiveRequested={() => documentType && promptArchive(documentType)}
        />
      )
    });
  };

  const promptArchive = (documentType: DocumentType) => {
    showModal({
      title: 'Archive document',
      message:
        'This document will not be available for selection in the future. Live workflows will be unaffected.',
      modalType: ConfirmationModalType.DEFAULT,
      confirmationText: 'Archive',
      onConfirm: async () => {
        showLoadingOverlay();
        try {
          await archiveDocumentType({ variables: { id: documentType.id } });
          showToast({ message: 'Document archived', type: ToastType.Success });
          closeDrawer();
          refetch();
        } catch (error) {
          showToast({
            message: extractErrorMessage(error),
            type: ToastType.Error
          });
        } finally {
          hideLoadingOverlay();
        }
      }
    });
  };

  const duplicateDocumentType = (documentType: DocumentType) => {
    openDrawer({
      width: '500px',
      children: (
        <DocumentTypeDrawer
          organizationId={organizationId}
          documentType={{
            ...documentType,
            id: '',
            // The duplicate is a fresh row so its name is editable even if the
            // source was locked from being used in a workflow.
            isUsedInCampaign: false,
            name: `${documentType.name} (copy)`
          }}
          onClose={closeDrawer}
          onSaved={() => {
            refetch();
            closeDrawer();
          }}
        />
      )
    });
  };

  return (
    <Box sx={styles.container}>
      <TextBlock
        variant="section"
        title="Documents"
        subtitle={
          <>
            These documents can be collected and validated in{' '}
            <Link
              href={workflowBuilderLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              workflows
            </Link>{' '}
            only.
          </>
        }
        sx={styles.header}
      />

      <Stack sx={styles.list} divider={<Divider />}>
        {loading && !documentTypes.length ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={styles.emptyState}
          >
            Loading…
          </Typography>
        ) : documentTypes.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={styles.emptyState}
          >
            No document types yet. Add one to get started.
          </Typography>
        ) : (
          documentTypes.map((documentType) => (
            <Stack
              key={documentType.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={styles.row}
            >
              <Box sx={styles.rowText}>
                <Typography variant="body1">{documentType.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatValidationSummary({
                    classificationRules: documentType.classificationRules,
                    validation: documentType.validation,
                    validationSettings: documentType.validationSettings
                  })}
                </Typography>
              </Box>
              <Stack direction="row" sx={styles.rowActions}>
                <IconTooltip title="Edit">
                  <IconButton
                    aria-label={`Edit ${documentType.name}`}
                    onClick={() => openEditDrawer(documentType)}
                    sx={styles.actionIcon}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </IconTooltip>
                <IconTooltip title="Duplicate">
                  <IconButton
                    aria-label={`Duplicate ${documentType.name}`}
                    onClick={() => duplicateDocumentType(documentType)}
                    sx={styles.actionIcon}
                  >
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </IconTooltip>
                <IconTooltip title="Archive">
                  <IconButton
                    aria-label={`Archive ${documentType.name}`}
                    onClick={() => promptArchive(documentType)}
                    sx={styles.actionIcon}
                  >
                    <Inventory2Icon fontSize="small" />
                  </IconButton>
                </IconTooltip>
              </Stack>
            </Stack>
          ))
        )}
      </Stack>

      <Box sx={styles.addButtonWrapper}>
        <Button variant="primary" onClick={() => openEditDrawer()}>
          Add new document type
        </Button>
      </Box>
    </Box>
  );
};
